import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import ContextChips, { CONTEXT_LINE } from '../ContextChips';
import DesktopFeatures from '../desktop/DesktopFeatures';
import Faq from '../Faq';
import { useInView } from '../hooks';
import { APP_URL, HOME_FAQ, absoluteUrl, faqSchema, softwareApplicationSchema } from '../seo';
import { TESTIMONIALS } from '../testimonials';
import './Home.css';

const HOME_TITLE = 'HorseStat - Data-Driven Horse Racing Analysis';
const HOME_DESCRIPTION =
  'HorseStat puts every UK & Irish runner\u2019s form, pace, going, class, trainer, jockey, sire and dam stats in one interactive racecard, ranked by a predictive rating from 10,000 race simulations.';
const HERO_VIDEO = {
  webm: '/videos/hero-desktop.webm',
  mp4: '/videos/hero-desktop.mp4',
  poster: '/videos/hero-desktop.jpg',
};

function TestimonialCards() {
  const [ref, inView] = useInView({ threshold: 0.25 });

  return (
    <div ref={ref} className={`testimonial-grid${inView ? ' is-in-view' : ''}`}>
      {TESTIMONIALS.map((item, index) => (
        <figure key={item.id} className="testimonial-card" style={{ '--card-index': index }}>
          <span className="testimonial-card__mark" aria-hidden="true">“</span>
          <p className="testimonial-card__pull">{item.pull}</p>
          <blockquote className="testimonial-card__quote">“{item.quote}”</blockquote>
          <figcaption className="testimonial-card__author">
            <span className="testimonial-card__avatar" aria-hidden="true">{item.initials}</span>
            <span className="testimonial-card__name">{item.author}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function tryStartMutedVideo(videoEl) {
  if (!videoEl) return;
  videoEl.muted = true;
  videoEl.defaultMuted = true;
  videoEl.setAttribute('muted', '');
  videoEl.setAttribute('playsinline', '');
  videoEl.play().catch(() => {});
}

function isMobileUserAgent() {
  return (
    typeof navigator !== 'undefined' &&
    (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  );
}

/* `features` swaps the block between the context band and testimonials (used by /mockups/desktop). */
function Home({ features = <DesktopFeatures /> }) {
  const [modalOpen, setModalOpen] = useState(false);
  const bgVideoRef = useRef(null);
  const isMobileDevice = isMobileUserAgent();

  useEffect(() => {
    if (!bgVideoRef.current) return;
    if (modalOpen) {
      bgVideoRef.current.pause();
    } else {
      tryStartMutedVideo(bgVideoRef.current);
    }
  }, [modalOpen]);

  useEffect(() => {
    const retryAutoplay = () => {
      const videos = document.querySelectorAll('.mockup-video');
      videos.forEach((videoEl) => {
        tryStartMutedVideo(videoEl);
      });
    };

    retryAutoplay();
    const rafId = window.requestAnimationFrame(retryAutoplay);
    const timeoutId = window.setTimeout(retryAutoplay, 250);
    document.addEventListener('visibilitychange', retryAutoplay);

    if (!isMobileDevice) {
      return () => {
        window.cancelAnimationFrame(rafId);
        window.clearTimeout(timeoutId);
        document.removeEventListener('visibilitychange', retryAutoplay);
      };
    }

    // iOS often allows autoplay only after gesture; these listeners "unlock"
    // muted playback as soon as the user touches or scrolls.
    window.addEventListener('touchstart', retryAutoplay, { passive: true });
    window.addEventListener('pointerdown', retryAutoplay, { passive: true });
    window.addEventListener('scroll', retryAutoplay, { passive: true });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', retryAutoplay);
      window.removeEventListener('touchstart', retryAutoplay);
      window.removeEventListener('pointerdown', retryAutoplay);
      window.removeEventListener('scroll', retryAutoplay);
    };
  }, [isMobileDevice]);

  return (
    <div className="home">
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <link rel="canonical" href={absoluteUrl('/')} />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:url" content={absoluteUrl('/')} />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema(HOME_FAQ))}</script>
      </Helmet>
      <section className="hero">
        <div className="hero-glow-1" />
        <div className="hero-glow-2" />
        <div className="hero-glow-3" />
        <div className="hero-content">
          <h1 className="hero-heading">
            Bet with more <span className="hero-badge">confidence</span>
            <br />
            than ever before
          </h1>
          <p className="hero-sub">
            Understand races the way professionals do, by seeing how form, stats
            and conditions combine to find winners.
          </p>
          <a
            href="https://app.horsestat.com"
            className="hero-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Started For Free
          </a>

          <div className="hero-mockup-wrap">
            <div
              className="hero-mockup-frame mockup-clickable"
              onClick={() => setModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setModalOpen(true)}
              aria-label="Watch product walkthrough"
            >
              <div className="hero-mockup-chrome">
                {/* <div className="mockup-chrome-bar">
                  <div className="mockup-chrome-dots">
                    <span className="dot dot-red" />
                    <span className="dot dot-yellow" />
                    <span className="dot dot-green" />
                  </div>
                </div> */}
                <div className="mockup-screen">
                  <video
                    className="mockup-video"
                    ref={bgVideoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={HERO_VIDEO.poster}
                    aria-label="HorseStat product walkthrough (muted preview)"
                    onLoadedData={(e) => {
                      tryStartMutedVideo(e.currentTarget);
                    }}
                    onCanPlay={(e) => {
                      tryStartMutedVideo(e.currentTarget);
                    }}
                  >
                    <source src={HERO_VIDEO.webm} type="video/webm" />
                    <source src={HERO_VIDEO.mp4} type="video/mp4" />
                  </video>
                  <div className="mockup-play-overlay">
                    <div className="mockup-play-btn">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M8 5.14v14l11-7-11-7z" />
                      </svg>
                    </div>
                    <span className="mockup-play-label">Watch with sound</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="context-band">
        <div className="context-band-inner container">
          <h2>
            Profitable betting starts with
            <br />
            <span className="context-accent">better context.</span>
          </h2>
          <p>{CONTEXT_LINE}</p>
          <ContextChips />
        </div>
        <div className="wave-bottom">
          <svg
            viewBox="0 0 1440 160"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path d="M0,0 C360,160 1080,160 1440,0" fill="#611f69" />
          </svg>
        </div>
      </section>

      {features}

      <section className="testimonials-band">
        <div className="wave-top">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0,120 C360,0 1080,0 1440,120 L1440,121 L0,121 Z"
              fill="#611f69"
            />
          </svg>
        </div>
        <div className="testimonials-inner container">
          <p className="testimonials-eyebrow">Trusted by racing fans</p>
          <h2 className="testimonials-title">Punters who see the whole race.</h2>
          <TestimonialCards />
          <div className="testimonials-cta">
            <a
              href={APP_URL}
              className="join-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Join Now
            </a>
          </div>
        </div>
      </section>

      <Faq />

      {modalOpen && (
        <div
          className="video-modal-overlay"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="video-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="video-modal-close"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <video
              className="video-modal-video"
              src={HERO_VIDEO.mp4}
              poster={HERO_VIDEO.poster}
              autoPlay
              muted
              controls
              onLoadedMetadata={(e) => {
                const isMobileViewport = window.matchMedia('(max-width: 900px)').matches;
                if (isMobileViewport) {
                  e.currentTarget.muted = true;
                  e.currentTarget.volume = 0;
                  return;
                }

                e.currentTarget.muted = false;
                e.currentTarget.volume = 0.05;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
